/* Horse Shop CY wishlist — restored working version with bulk cart and clear-all actions. */
(() => {
  const storageKey = 'horseshopcy:wishlist:v1';
  const sharedWishlistUrl = `${window.Shopify.routes.root}apps/wishlist/api/me`;
  let sharedWishlistActive = false;
  let sharedWishlistLoaded = false;
  let sharedWishlistPublic = false;
  let sharedWishlistTimer;
  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey));
      return Array.isArray(value) ? value : [];
    } catch (_error) {
      return [];
    }
  };
  const write = (items, { sync = true, skipPageRender = false } = {}) => {
    localStorage.setItem(storageKey, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items, skipPageRender } }));
    if (sync) scheduleSharedWishlistSave();
  };
  const keyFor = (item) => `${item.handle}:${item.variantId || ''}`;

  const uniqueItems = (items) => {
    const unique = new Map();
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (!item?.handle || !item?.variantId) return;
      const key = keyFor(item);
      const existing = unique.get(key);
      unique.set(key, {
        ...existing,
        ...item,
        quantity: Math.max(1, Math.min(99, parseInt(item.quantity, 10) || 1)),
        fulfilledQuantity: Math.max(0, Number(existing?.fulfilledQuantity) || 0, Number(item.fulfilledQuantity) || 0),
        purchasedByOwner: Boolean(existing?.purchasedByOwner || item.purchasedByOwner),
        pinned: Boolean(existing?.pinned || item.pinned)
      });
    });
    return [...unique.values()];
  };
  const pinnedFirst = (items) => items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => Number(Boolean(b.item.pinned)) - Number(Boolean(a.item.pinned)) || a.index - b.index)
    .map(({ item }) => item);

  const wishlistFingerprint = (items) => JSON.stringify(uniqueItems(items).map((item) => ({
    handle: String(item.handle),
    variantId: String(item.variantId),
    quantity: Number(item.quantity) || 1,
    fulfilledQuantity: Number(item.fulfilledQuantity) || 0,
    purchasedByOwner: Boolean(item.purchasedByOwner),
    pinned: Boolean(item.pinned)
  })));

  const scheduleSharedWishlistSave = () => {
    if (!sharedWishlistLoaded || !sharedWishlistActive) return;
    window.clearTimeout(sharedWishlistTimer);
    sharedWishlistTimer = window.setTimeout(async () => {
      try {
        const response = await fetch(sharedWishlistUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: read(), isPublic: sharedWishlistPublic })
        });
        if (!response.ok) return;
        const saved = await response.json();
        sharedWishlistPublic = Boolean(saved.isPublic);
      } catch (_error) {
        // Local storage remains the fallback if the shared service is unavailable.
      }
    }, 250);
  };
  const setSharedWishlistPublic = (isPublic) => {
    sharedWishlistPublic = Boolean(isPublic);
    scheduleSharedWishlistSave();
    document.dispatchEvent(new CustomEvent('wishlist:privacy-change', { detail: { isPublic: sharedWishlistPublic } }));
  };

  const loadSharedWishlist = async () => {
    try {
      const response = await fetch(sharedWishlistUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const saved = await response.json();
      sharedWishlistActive = true;
      sharedWishlistPublic = Boolean(saved.isPublic);
      // For signed-in customers, the server is authoritative. Merging the
      // device's stale local copy here would resurrect items deleted elsewhere.
      const serverItems = uniqueItems(saved.items || []);
      const itemsChanged = wishlistFingerprint(read()) !== wishlistFingerprint(serverItems);
      if (itemsChanged) write(serverItems, { sync: false, skipPageRender: true });
      sharedWishlistLoaded = true;
      document.dispatchEvent(new CustomEvent('wishlist:account-ready', { detail: { isPublic: sharedWishlistPublic } }));
      return itemsChanged;
    } catch (_error) {
      // Guests, and customers temporarily offline, continue using device storage.
      return false;
    } finally {
      sharedWishlistLoaded = true;
    }
  };

  const updateControls = (items = read()) => {
    const keys = new Set(items.map(keyFor));
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const saved = button.dataset.variantId
        ? keys.has(`${button.dataset.productHandle}:${button.dataset.variantId}`)
        : items.some((item) => item.handle === button.dataset.productHandle);
      const title = button.dataset.productTitle || 'product';
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${title} ${saved ? 'from' : 'to'} wishlist`);
      button.title = saved ? 'Remove from wishlist' : 'Save to wishlist';
    });
    document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
      count.textContent = items.length > 99 ? '99+' : String(items.length);
      count.classList.toggle('wishlist-count-bubble--large', items.length > 99);
      count.hidden = items.length === 0;
    });
  };

  const hydrateButtonProduct = async (button) => {
    const productInfo = button.closest('product-info');
    const selectedVariantInput = productInfo?.querySelector(
      'form[action*="/cart/add"] input[name="id"]'
    );
    if (selectedVariantInput?.value) {
      button.dataset.variantId = selectedVariantInput.value;
      return true;
    }
    if (button.dataset.variantId) return true;
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}products/${encodeURIComponent(button.dataset.productHandle)}.js`,
        { headers: { Accept: 'application/json' } }
      );
      if (!response.ok) return false;
      const product = await response.json();
      const variant = product.variants.find((entry) => entry.available) || product.variants[0];
      button.dataset.productId = product.id;
      button.dataset.variantId = variant.id;
      button.dataset.productTitle = product.title;
      return true;
    } catch (_error) {
      return false;
    }
  };

  const toggle = async (button) => {
    if (!await hydrateButtonProduct(button)) return;
    const item = {
      handle: button.dataset.productHandle,
      productId: button.dataset.productId,
      variantId: button.dataset.variantId,
      quantity: 1
    };
    const items = read();
    const index = items.findIndex((entry) => keyFor(entry) === keyFor(item));
    const wishlistCard = button.closest('[data-wishlist-page] .wishlist-item');
    if (index >= 0) {
      items.splice(index, 1);
      write(items, { skipPageRender: Boolean(wishlistCard) });

      if (wishlistCard) {
        const root = wishlistCard.closest('[data-wishlist-page]');
        wishlistCard.remove();
        root.wishlistAvailableVariantIds = (root.wishlistAvailableVariantIds || [])
          .filter((id) => String(id) !== String(item.variantId));

        const empty = root.querySelector('[data-wishlist-empty]');
        const catalogue = root.querySelector('[data-wishlist-catalogue]');
        const toolbar = root.querySelector('[data-wishlist-toolbar]');
        const addAllButton = root.querySelector('[data-wishlist-add-all]');
        const clearButton = root.querySelector('[data-wishlist-clear]');
        const status = root.querySelector('[data-wishlist-status]');
        const hasItems = items.length > 0;

        if (empty) empty.hidden = hasItems;
        if (catalogue) catalogue.hidden = !hasItems;
        if (toolbar) toolbar.hidden = !hasItems;
        if (clearButton) clearButton.disabled = !hasItems;
        if (addAllButton) addAllButton.disabled = !root.wishlistAvailableVariantIds.length;
        if (status) status.textContent = hasItems
          ? `${items.length} saved ${items.length === 1 ? 'item' : 'items'}`
          : '';
      }
    } else {
      items.push(item);
      write(items);
    }
  };

  const togglePinned = (button) => {
    const items = read();
    const index = items.findIndex((entry) => keyFor(entry) === `${button.dataset.productHandle}:${button.dataset.variantId}`);
    if (index < 0) return;
    items[index] = { ...items[index], pinned: !items[index].pinned };
    const pinned = items[index].pinned;
    button.classList.toggle('is-pinned', pinned);
    button.setAttribute('aria-pressed', String(pinned));
    button.setAttribute('aria-label', `${pinned ? 'Unpin' : 'Pin'} item`);
    button.title = pinned ? 'Unpin item' : 'Pin item';
    button.closest('.wishlist-item')?.classList.toggle('is-pinned-item', pinned);
    write(items, { skipPageRender: true });
  };

  const updateQuantity = (button, value) => {
    const items = read();
    const index = items.findIndex((entry) => keyFor(entry) === `${button.dataset.productHandle}:${button.dataset.variantId}`);
    if (index < 0) return;
    const card = button.closest('.wishlist-item');
    const input = card?.querySelector('[data-wishlist-quantity] input');
    const fulfilled = Math.max(0, parseInt(items[index].fulfilledQuantity, 10) || 0);
    const maximum = Math.max(1, parseInt(input?.max, 10) || 99);
    const minimum = Math.min(maximum, Math.max(1, fulfilled));
    const quantity = Math.max(minimum, Math.min(maximum, parseInt(value, 10) || 1));
    items[index] = { ...items[index], quantity };
    if (input) {
      input.value = String(quantity);
      const controls = input.closest('.wishlist-item__quantity-control');
      const decrease = controls?.querySelector('[data-wishlist-quantity-change="-1"]');
      const increase = controls?.querySelector('[data-wishlist-quantity-change="1"]');
      if (decrease) decrease.disabled = quantity <= minimum;
      if (increase) increase.disabled = quantity >= maximum;
    }
    write(items, { skipPageRender: true });
  };

  const enhanceFeaturedProducts = (root = document) => {
    root.querySelectorAll('#hs-featured-gear .hs-card').forEach((card) => {
      if (card.querySelector('[data-wishlist-toggle]')) return;
      const productLink = card.querySelector('.hs-card__media[href]');
      if (!productLink) return;
      const match = productLink.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!match) return;
      const title = card.querySelector('.hs-name')?.textContent.trim() || 'product';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wishlist-button';
      button.dataset.wishlistToggle = '';
      button.dataset.productHandle = decodeURIComponent(match[1]);
      button.dataset.productBaseTitle = title;
      button.dataset.productTitle = title;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', `Save ${title} to wishlist`);
      button.title = 'Save to wishlist';
      button.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="M12 20.4 3.7 12.7A5.3 5.3 0 0 1 11.2 5l.8.9.8-.9a5.3 5.3 0 0 1 7.5 7.7L12 20.4Z"></path></svg>';
      card.querySelector('.hs-card__imagewrap')?.prepend(button);
    });
  };

  const formatMoney = (cents, currency) => new Intl.NumberFormat(
    document.documentElement.lang || 'en',
    { style: 'currency', currency }
  ).format(cents / 100);

  const productImageUrl = (url) => {
    if (!url) return '';
    return url.replace(/(\.[a-z]+)(\?.*)?$/i, '_533x$1$2');
  };

  const variantOptions = (product, variant) => {
    const values = variant.options || [variant.option1, variant.option2, variant.option3];
    return (product.options || []).map((option, index) => ({
      name: typeof option === 'string' ? option : option.name,
      value: values[index]
    }));
  };

  const swatchColor = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    const namedColors = {
      pink: '#ff1493',
      'black cherry': '#4b1525',
      berry: '#9b315d',
      'light berry': '#bd4f79',
      walnut: '#756044',
      navy: '#243c5a',
      burgundy: '#713247',
      turquoise: '#4faaa6',
      mint: '#a9d8cf',
      teal: '#357c78',
      camel: '#b58b5a',
      beige: '#d5c5a5',
      cream: '#eee5cf',
      grey: '#8b9297',
      gray: '#8b9297'
    };
    if (namedColors[normalized]) return namedColors[normalized];
    const matchedName = Object.keys(namedColors).find((name) => normalized.includes(name));
    if (matchedName) return namedColors[matchedName];
    if (window.CSS?.supports?.('color', normalized)) return normalized;
    return '#777';
  };

  const renderPage = async () => {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;
    const renderId = (root.wishlistRenderId || 0) + 1;
    root.wishlistRenderId = renderId;
    const grid = root.querySelector('[data-wishlist-grid]');
    const empty = root.querySelector('[data-wishlist-empty]');
    const catalogue = root.querySelector('[data-wishlist-catalogue]');
    const toolbar = root.querySelector('[data-wishlist-toolbar]');
    const addAllButton = root.querySelector('[data-wishlist-add-all]');
    const clearButton = root.querySelector('[data-wishlist-clear]');
    const status = root.querySelector('[data-wishlist-status]');
    const items = pinnedFirst(read());
    root.wishlistObserver?.disconnect();
    if (root.wishlistScrollHandler) {
      window.removeEventListener('scroll', root.wishlistScrollHandler);
      root.wishlistScrollHandler = null;
    }
    if (root.wishlistIntentHandlers) {
      window.removeEventListener('wheel', root.wishlistIntentHandlers.mark);
      window.removeEventListener('touchstart', root.wishlistIntentHandlers.mark);
      window.removeEventListener('keydown', root.wishlistIntentHandlers.keydown);
      root.wishlistIntentHandlers = null;
    }
    root.querySelector('[data-wishlist-load-more]')?.remove();
    root.wishlistAvailableVariantIds = [];
    grid.replaceChildren();
    empty.hidden = items.length > 0;
    catalogue.hidden = items.length === 0;
    toolbar.hidden = items.length === 0;
    clearButton.disabled = items.length === 0;
    addAllButton.disabled = true;
    if (!items.length) {
      status.textContent = '';
      return;
    }

    status.textContent = 'Loading your saved items…';
    const pageSize = 15;
    const productRequests = new Map();
    const swatchRequests = new Map();
    const inventoryRequests = new Map();
    const cartRequest = fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : { items: [] })
      .catch(() => ({ items: [] }));
    const fetchSavedItem = async (saved) => {
      try {
        if (!productRequests.has(saved.handle)) {
          const url = `${window.Shopify.routes.root}products/${encodeURIComponent(saved.handle)}.js`;
          productRequests.set(saved.handle, fetch(url, { headers: { Accept: 'application/json' } })
            .then((response) => response.ok ? response.json() : null));
        }
        const product = await productRequests.get(saved.handle);
        if (!product) return null;
        if (!inventoryRequests.has(saved.handle)) {
          const inventoryUrl = `${window.Shopify.routes.root}products/${encodeURIComponent(saved.handle)}?view=wishlist-inventory`;
          inventoryRequests.set(saved.handle, fetch(inventoryUrl, { headers: { Accept: 'application/json' } })
            .then((response) => response.ok ? response.json() : { variants: {} })
            .catch(() => ({ variants: {} })));
        }
        if (!swatchRequests.has(saved.handle)) {
          const swatchUrl = `${window.Shopify.routes.root}products/${encodeURIComponent(saved.handle)}?view=wishlist-swatches`;
          swatchRequests.set(saved.handle, fetch(swatchUrl, { headers: { Accept: 'text/html' } })
            .then((response) => response.ok ? response.json() : [])
            .catch(() => []));
        }
        // Product JSON is enough to paint the card. Inventory and swatches are
        // enhancements and must not hold up the first visible render.
        return {
          saved,
          product,
          inventoryPromise: inventoryRequests.get(saved.handle),
          swatchesPromise: swatchRequests.get(saved.handle)
        };
      } catch (_error) {
        return null;
      }
    };
    status.textContent = `${items.length} saved ${items.length === 1 ? 'item' : 'items'}`;
    const [results, cart] = await Promise.all([
      Promise.all(items.slice(0, pageSize).map(fetchSavedItem)),
      cartRequest
    ]);
    if (root.wishlistRenderId !== renderId) return;
    const cartVariantIds = new Set((cart.items || []).map((item) => String(item.variant_id || item.id)));
    root.wishlistCartVariantIds = cartVariantIds;
    const valid = results.filter(Boolean);
    empty.hidden = true;
    catalogue.hidden = false;
    toolbar.hidden = false;

    const registerAvailableVariant = ({ saved, product }) => {
      const variant = product.variants.find((entry) => String(entry.id) === String(saved.variantId))
        || product.variants[0];
      if (variant.available && !cartVariantIds.has(String(variant.id))) {
        root.wishlistAvailableVariantIds.push(variant.id);
      }
    };
    valid.forEach(registerAvailableVariant);

    let remainingResolved = items.length <= pageSize;
    const remainingResultsPromise = remainingResolved
      ? Promise.resolve([])
      : Promise.all(items.slice(pageSize).map(fetchSavedItem)).then((remainingResults) => {
          if (root.wishlistRenderId !== renderId) return [];
          const remainingValid = remainingResults.filter(Boolean);
          remainingValid.forEach(registerAvailableVariant);
          valid.push(...remainingValid);
          remainingResolved = true;
          addAllButton.disabled = root.wishlistAvailableVariantIds.length === 0;
          return remainingValid;
        });

    const renderItem = ({ saved, product, inventoryPromise, swatchesPromise }, revealIndex = 0) => {
      const variant = product.variants.find((entry) => String(entry.id) === String(saved.variantId))
        || product.variants[0];
      const quantityMaximum = 99;
      const savedQuantity = Math.min(quantityMaximum, Math.max(1, parseInt(saved.quantity, 10) || 1));
      const isAlreadyInCart = cartVariantIds.has(String(variant.id));
      const url = `${window.Shopify.routes.root}products/${product.handle}?variant=${variant.id}`;
      const image = variant.featured_image?.src || product.featured_image || '';
      const item = document.createElement('li');
      item.className = `grid__item wishlist-item${saved.pinned ? ' is-pinned-item' : ''}${variant.available ? '' : ' wishlist-item--sold-out'}`;
      item.style.setProperty('--wishlist-reveal-delay', `${Math.min(revealIndex, pageSize - 1) * 24}ms`);
      item.innerHTML = `
        <div class="wishlist-item__media">
          <button class="wishlist-pin-button${saved.pinned ? ' is-pinned' : ''}" type="button" data-wishlist-pin
            data-product-handle="${product.handle}" data-variant-id="${variant.id}"
            aria-pressed="${saved.pinned ? 'true' : 'false'}"
            aria-label="${saved.pinned ? 'Unpin' : 'Pin'} ${product.title}"
            title="${saved.pinned ? 'Unpin item' : 'Pin item'}">
            <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d="m12 2.8 2.8 5.7 6.3.9-4.6 4.4 1.1 6.3-5.6-3-5.6 3 1.1-6.3-4.6-4.4 6.3-.9L12 2.8Z"></path></svg>
          </button>
          <a class="wishlist-item__image-link">
            ${image ? '<img class="wishlist-item__image" alt="">' : ''}
          </a>
          ${variant.available ? '' : '<span class="wishlist-item__sold-out-badge">Sold out</span>'}
          <button class="wishlist-button is-saved" type="button" data-wishlist-toggle
            data-product-handle="${product.handle}" data-product-id="${product.id}"
            data-variant-id="${variant.id}" data-product-base-title=""
            aria-pressed="true" aria-label="Remove from wishlist" title="Remove from wishlist">
            <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
              <path d="M12 20.4 3.7 12.7A5.3 5.3 0 0 1 11.2 5l.8.9.8-.9a5.3 5.3 0 0 1 7.5 7.7L12 20.4Z"></path>
            </svg>
          </button>
        </div>
        <div class="wishlist-item__quick-add">
          <button class="button button--primary" type="button" data-wishlist-add
            data-variant-id="${variant.id}" ${variant.available && !isAlreadyInCart ? '' : 'disabled'}>
            <span class="wishlist-item__quick-add-text">${!variant.available ? 'Sold out' : isAlreadyInCart ? 'Already in cart' : 'Add to cart'}</span>
          </button>
        </div>
        <div class="wishlist-item__information">
          <h2 class="wishlist-item__title h4"><a class="full-unstyled-link"></a></h2>
          <p class="wishlist-item__vendor"></p>
          <p class="wishlist-item__price"></p>
          <div class="wishlist-item__quantity" data-wishlist-quantity><span>Wanted</span><div class="wishlist-item__quantity-control"><button type="button" data-wishlist-quantity-change="-1" aria-label="Decrease wanted quantity" ${savedQuantity <= 1 ? 'disabled' : ''}>−</button><input type="number" min="1" max="${quantityMaximum}" value="${savedQuantity}" aria-label="Wanted quantity"><button type="button" data-wishlist-quantity-change="1" aria-label="Increase wanted quantity" ${savedQuantity >= quantityMaximum ? 'disabled' : ''}>+</button></div></div>
        </div>`;
      const imageLink = item.querySelector('.wishlist-item__image-link');
      imageLink.href = url;
      const imageElement = item.querySelector('.wishlist-item__image');
      if (imageElement) {
        imageElement.src = productImageUrl(image);
        imageElement.alt = product.title;
      }
      const titleLink = item.querySelector('.wishlist-item__title a');
      titleLink.href = url;
      titleLink.textContent = product.title;
      const vendor = item.querySelector('.wishlist-item__vendor');
      vendor.textContent = product.vendor || '';
      vendor.hidden = root.dataset.showVendor !== 'true' || !product.vendor;
      const options = variantOptions(product, variant);
      const colorOption = options.find((option) => /colou?r/i.test(option.name || ''));
      const sizeOption = options.find((option) => /size/i.test(option.name || ''));
      if (sizeOption?.value) {
        const sizeBadge = document.createElement('span');
        sizeBadge.className = 'wishlist-item__size-badge';
        sizeBadge.textContent = sizeOption.value;
        sizeBadge.setAttribute('aria-label', `Size ${sizeOption.value}`);
        imageLink.appendChild(sizeBadge);
      }
      if (colorOption?.value) {
        const colorSwatch = document.createElement('span');
        colorSwatch.className = 'wishlist-item__color-swatch';
        colorSwatch.style.background = swatchColor(colorOption.value);
        colorSwatch.dataset.colourLabel = colorOption.value;
        colorSwatch.setAttribute('aria-label', `Color ${colorOption.value}`);
        imageLink.appendChild(colorSwatch);
        swatchesPromise?.then((swatches) => {
          if (!colorSwatch.isConnected || !Array.isArray(swatches)) return;
          const configuredSwatch = swatches.find((swatch) =>
            String(swatch.option).toLowerCase() === String(colorOption.name).toLowerCase()
            && String(swatch.value).toLowerCase() === String(colorOption.value).toLowerCase()
          );
          if (configuredSwatch?.background) colorSwatch.style.background = configuredSwatch.background;
        });
      }
      item.querySelector('.wishlist-item__price').textContent = formatMoney(variant.price, root.dataset.currency);
      const wantedQuantity = savedQuantity;
      const fulfilledQuantity = Math.min(wantedQuantity, Math.max(0, parseInt(saved.fulfilledQuantity, 10) || 0));
      if (!saved.purchasedByOwner && fulfilledQuantity >= wantedQuantity) item.classList.add('wishlist-item--fulfilled');
      if (saved.purchasedByOwner || fulfilledQuantity > 0) {
        const badge = document.createElement('span');
        badge.className = 'wishlist-item__fulfillment-badge';
        badge.textContent = saved.purchasedByOwner
          ? 'Purchased'
          : fulfilledQuantity >= wantedQuantity
            ? 'Fulfilled'
            : `${fulfilledQuantity}/${wantedQuantity} fulfilled`;
        if (!saved.purchasedByOwner && fulfilledQuantity < wantedQuantity) {
          const remainingLabel = `${wantedQuantity - fulfilledQuantity} Remaining`;
          badge.dataset.tooltip = remainingLabel;
          badge.setAttribute('aria-label', `${badge.textContent}, ${remainingLabel}`);
          badge.tabIndex = 0;
        }
        const badgeRow = document.createElement('span');
        badgeRow.className = 'wishlist-item__badge-row';
        const sizeBadge = imageLink.querySelector('.wishlist-item__size-badge');
        if (sizeBadge) badgeRow.appendChild(sizeBadge);
        badgeRow.appendChild(badge);
        imageLink.appendChild(badgeRow);
      }
      const addButton = item.querySelector('[data-wishlist-add]');
      if (root.dataset.quickAdd !== 'true') {
        item.querySelector('.wishlist-item__quick-add').remove();
      } else addButton.addEventListener('click', async (event) => {
        const button = event.currentTarget;
        button.disabled = true;
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(cartAddPayload([{ id: variant.id, quantity: 1 }]))
          });
          const buttonText = button.querySelector('.wishlist-item__quick-add-text');
          if (response.ok) {
            await updateCartIcon(response);
            cartVariantIds.add(String(variant.id));
            root.wishlistAvailableVariantIds = root.wishlistAvailableVariantIds
              .filter((id) => String(id) !== String(variant.id));
            buttonText.textContent = 'Already in cart';
          } else {
            buttonText.textContent = 'Could not add';
            button.disabled = false;
          }
        } catch (_error) {
          button.querySelector('.wishlist-item__quick-add-text').textContent = 'Could not add';
          button.disabled = false;
        }
      });
      grid.appendChild(item);
      inventoryPromise?.then((inventory) => {
        if (!item.isConnected || root.wishlistRenderId !== renderId) return;
        const maximumValue = Number(inventory?.variants?.[String(variant.id)]?.max);
        if (!Number.isFinite(maximumValue)) return;
        const maximum = Math.max(1, maximumValue);
        const input = item.querySelector('[data-wishlist-quantity] input');
        if (!input) return;
        input.max = String(maximum);
        if (Number(input.value) > maximum) input.value = String(maximum);
        const increase = item.querySelector('[data-wishlist-quantity-change="1"]');
        if (increase) increase.disabled = Number(input.value) >= maximum;
      });
    };

    let renderedCount = 0;

    const stopLoadingMore = () => {
      root.wishlistObserver?.disconnect();
      if (root.wishlistScrollHandler) {
        window.removeEventListener('scroll', root.wishlistScrollHandler);
        root.wishlistScrollHandler = null;
      }
      if (root.wishlistIntentHandlers) {
        window.removeEventListener('wheel', root.wishlistIntentHandlers.mark);
        window.removeEventListener('touchstart', root.wishlistIntentHandlers.mark);
        window.removeEventListener('keydown', root.wishlistIntentHandlers.keydown);
        root.wishlistIntentHandlers = null;
      }
      root.querySelector('[data-wishlist-load-more]')?.remove();
    };

    const loadMore = async () => {
      if (renderedCount >= valid.length && !remainingResolved) await remainingResultsPromise;
      if (root.wishlistRenderId !== renderId) return;
      const nextItems = valid.slice(renderedCount, renderedCount + pageSize);
      nextItems.forEach((entry, index) => renderItem(entry, index));
      renderedCount += nextItems.length;
      if (remainingResolved && renderedCount >= valid.length) stopLoadingMore();
    };

    loadMore();
    if (items.length > pageSize) {
      const loadMoreTrigger = document.createElement('div');
      loadMoreTrigger.className = 'wishlist-page__load-more';
      loadMoreTrigger.dataset.wishlistLoadMore = '';
      loadMoreTrigger.setAttribute('role', 'status');
      loadMoreTrigger.setAttribute('aria-live', 'polite');
      loadMoreTrigger.innerHTML = `
        <span class="wishlist-page__loading-spinner" aria-hidden="true"></span>
        <span>Loading more products&hellip;</span>`;
      grid.after(loadMoreTrigger);

      let previousScrollPosition = window.scrollY;
      let isLoadingMore = false;
      let hasUserScrollIntent = false;
      const markUserScrollIntent = () => { hasUserScrollIntent = true; };
      const markKeyboardScrollIntent = (event) => {
        if (['ArrowDown', 'PageDown', 'End', ' '].includes(event.key)) hasUserScrollIntent = true;
      };
      const checkLoadMorePosition = () => {
        const currentScrollPosition = window.scrollY;
        const isScrollingDown = currentScrollPosition > previousScrollPosition;
        previousScrollPosition = currentScrollPosition;
        if (!hasUserScrollIntent || !isScrollingDown || isLoadingMore || !loadMoreTrigger.isConnected || (remainingResolved && renderedCount >= valid.length)) return;
        if (loadMoreTrigger.getBoundingClientRect().top <= window.innerHeight + 120) {
          hasUserScrollIntent = false;
          isLoadingMore = true;
          loadMoreTrigger.classList.add('is-loading');
          window.setTimeout(() => {
            if (root.wishlistRenderId !== renderId) return;
            loadMoreTrigger.classList.add('is-revealing');
            window.setTimeout(async () => {
              if (root.wishlistRenderId !== renderId) return;
              await loadMore();
              hasUserScrollIntent = false;
              isLoadingMore = false;
              loadMoreTrigger.classList.remove('is-loading', 'is-revealing');
              previousScrollPosition = window.scrollY;
            }, 120);
          }, 120);
        }
      };
      root.wishlistScrollHandler = checkLoadMorePosition;
      root.wishlistIntentHandlers = { mark: markUserScrollIntent, keydown: markKeyboardScrollIntent };
      window.addEventListener('wheel', markUserScrollIntent, { passive: true });
      window.addEventListener('touchstart', markUserScrollIntent, { passive: true });
      window.addEventListener('keydown', markKeyboardScrollIntent);
      window.addEventListener('scroll', checkLoadMorePosition, { passive: true });
    }
    addAllButton.disabled = !remainingResolved || root.wishlistAvailableVariantIds.length === 0;
  };

  const cartAddPayload = (items) => ({
    items,
    sections: ['cart-icon-bubble'],
    sections_url: window.location.pathname
  });

  const updateCartIcon = async (response) => {
    try {
      const cartData = await response.json();
      const sectionHtml = cartData.sections?.['cart-icon-bubble'];
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (!sectionHtml || !cartIcon) return;
      const renderedSection = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('.shopify-section');
      if (renderedSection) cartIcon.innerHTML = renderedSection.innerHTML;
    } catch (_error) {
      // The cart addition still succeeded even if its header section could not be rendered.
    }
  };

  const addVariantsToCart = async (variantIds) => fetch(`${window.Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(cartAddPayload(variantIds.map((id) => ({ id, quantity: 1 }))))
  });

  document.addEventListener('pointerdown', (event) => {
    if (event.target.closest('[data-wishlist-toggle]')) event.stopPropagation();
  }, true);

  let collectionSwatchTooltip;
  let activeCollectionSwatch;
  const hideCollectionSwatchTooltip = () => {
    collectionSwatchTooltip?.remove();
    collectionSwatchTooltip = null;
    activeCollectionSwatch = null;
  };
  const showCollectionSwatchTooltip = (swatch) => {
    if (swatch === activeCollectionSwatch) return;
    hideCollectionSwatchTooltip();
    activeCollectionSwatch = swatch;
    collectionSwatchTooltip = document.createElement('span');
    collectionSwatchTooltip.className = 'card-colour-tooltip';
    collectionSwatchTooltip.textContent = swatch.dataset.colourLabel;
    document.body.appendChild(collectionSwatchTooltip);
    const swatchRect = swatch.getBoundingClientRect();
    const tooltipRect = collectionSwatchTooltip.getBoundingClientRect();
    collectionSwatchTooltip.style.left = `${Math.max(8, Math.min(
      window.innerWidth - tooltipRect.width - 8,
      swatchRect.left + (swatchRect.width - tooltipRect.width) / 2
    ))}px`;
    collectionSwatchTooltip.style.top = `${Math.max(8, swatchRect.top - tooltipRect.height - 8)}px`;
  };
  document.addEventListener('mousemove', (event) => {
    const card = event.target.closest('.card-wrapper, .wishlist-item');
    if (!card) {
      hideCollectionSwatchTooltip();
      return;
    }
    const swatches = card.querySelectorAll(
      '.card-colour-swatches__swatch[data-colour-label], .wishlist-item__color-swatch[data-colour-label]'
    );
    const hoveredSwatch = Array.from(swatches).find((swatch) => {
      const rect = swatch.getBoundingClientRect();
      return event.clientX >= rect.left - 3 && event.clientX <= rect.right + 3
        && event.clientY >= rect.top - 3 && event.clientY <= rect.bottom + 3;
    });
    if (hoveredSwatch) showCollectionSwatchTooltip(hoveredSwatch);
    else hideCollectionSwatchTooltip();
  });
  document.addEventListener('mouseleave', hideCollectionSwatchTooltip);
  window.addEventListener('scroll', hideCollectionSwatchTooltip, { passive: true });
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    await toggle(button);
  }, true);
  document.addEventListener('click', async (event) => {
    const addAllButton = event.target.closest('[data-wishlist-add-all]');
    if (addAllButton) {
      const root = addAllButton.closest('[data-wishlist-page]');
      const variantIds = root?.wishlistAvailableVariantIds || [];
      if (!variantIds.length) return;
      const labelElement = addAllButton.querySelector('.wishlist-page__toolbar-text');
      const originalLabel = labelElement.textContent.trim();
      addAllButton.disabled = true;
      labelElement.textContent = 'Adding…';
      try {
        const response = await addVariantsToCart(variantIds);
        labelElement.textContent = response.ok ? 'All available items added' : 'Could not add items';
        if (response.ok) {
          await updateCartIcon(response);
          variantIds.forEach((id) => root.wishlistCartVariantIds?.add(String(id)));
          root.wishlistAvailableVariantIds = [];
          root.querySelectorAll('[data-wishlist-add]').forEach((button) => {
            if (!variantIds.some((id) => String(id) === String(button.dataset.variantId))) return;
            button.disabled = true;
            button.querySelector('.wishlist-item__quick-add-text').textContent = 'Already in cart';
          });
        } else addAllButton.disabled = false;
      } catch (_error) {
        labelElement.textContent = 'Could not add items';
        addAllButton.disabled = false;
      }
      window.setTimeout(() => {
        labelElement.textContent = originalLabel;
        addAllButton.disabled = root.wishlistAvailableVariantIds.length === 0;
      }, 2500);
      return;
    }
    const clearButton = event.target.closest('[data-wishlist-clear]');
    if (!clearButton) return;
    if (window.confirm('Remove all items from your wishlist?')) write([]);
  });

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-wishlist-quantity-change]');
    if (quantityButton) {
      const card = quantityButton.closest('.wishlist-item');
      const input = card?.querySelector('[data-wishlist-quantity] input');
      const wishlistButton = card?.querySelector('[data-wishlist-toggle]');
      if (input && wishlistButton) updateQuantity(wishlistButton, Number(input.value) + Number(quantityButton.dataset.wishlistQuantityChange));
      return;
    }
    const button = event.target.closest('[data-wishlist-pin]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    togglePinned(button);
  });
  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-wishlist-quantity] input');
    if (!input) return;
    const button = input.closest('.wishlist-item')?.querySelector('[data-wishlist-toggle]');
    if (button) updateQuantity(button, input.value);
  });
  document.addEventListener('wishlist:change', (event) => {
    updateControls(event.detail.items);
    if (!event.detail.skipPageRender && document.querySelector('[data-wishlist-page]')) renderPage();
  });
  document.addEventListener('shopify:section:load', (event) => {
    enhanceFeaturedProducts(event.target);
    updateControls();
    const loading = event.target.querySelector('[data-wishlist-loading]');
    if (loading) loading.hidden = true;
    renderPage();
  });
  window.addEventListener('storage', (event) => {
    if (event.key === storageKey) {
      updateControls();
      renderPage();
    }
  });
  const productInfo = document.querySelector('product-info');
  if (productInfo) {
    new MutationObserver(() => updateControls())
      .observe(productInfo, { childList: true, subtree: true });
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const button = document.querySelector(
        `product-info[data-section="${data.sectionId}"] [data-wishlist-toggle]`
      );
      if (!button || !data.variant) return;
      button.dataset.variantId = data.variant.id;
      const baseTitle = button.dataset.productBaseTitle || button.dataset.productTitle;
      button.dataset.productTitle = data.variant.public_title
        ? `${baseTitle} - ${data.variant.public_title}`
        : baseTitle;
      updateControls();
    });
  }
  enhanceFeaturedProducts();
  updateControls();
  const initialiseWishlist = async () => {
    const wishlistPage = document.querySelector('[data-wishlist-page]');
    if (!wishlistPage) {
      await loadSharedWishlist();
      return;
    }

    // Paint the device copy immediately. Account sync can happen in parallel;
    // it should not make returning visitors stare at placeholders.
    const accountWishlist = loadSharedWishlist();
    await renderPage();
    const loading = wishlistPage.querySelector('[data-wishlist-loading]');
    if (loading) loading.hidden = true;
    if (await accountWishlist) await renderPage();
  };
  initialiseWishlist();
  window.HorseShopWishlist = { read, write, toggle, setPublic: setSharedWishlistPublic };
})();
